import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useMenuData } from '@/context/MenuDataContext';
import { restaurantInfo } from '@/data/menuData';
import { trackEvent } from '@/lib/analytics';
import Header from './Header';
import Footer from './Footer';

const CategoryCard = ({ category, index }) => {
  const navigate = useNavigate();
  const { getText } = useLanguage();
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div
      onClick={() => { trackEvent('category_click', { category_id: category.id }); navigate(`/menu/${category.id}`); }}
      className="relative group cursor-pointer overflow-hidden rounded-xl aspect-[4/3] shadow-elegant hover-lift animate-fade-in-up"
      style={{ animationDelay: `${index * 0.03}s` }}
    >
      {/* Skeleton Loader */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={category.image}
          alt={getText(category.name)}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) {
              e.currentTarget.src = '/images/menu/placeholder.webp';
              setImageLoaded(true);
            }
          }}
        />
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

      {/* Gold accent line on hover */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="font-display text-lg sm:text-xl text-foreground mb-1 group-hover:text-primary transition-colors duration-300">
          {getText(category.name)}
        </h3>
        <p className="text-muted-foreground text-xs sm:text-sm font-body line-clamp-1 opacity-80">
          {getText(category.description)}
        </p>
      </div>

      {/* Corner accent */}
      <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-primary/30 rounded-tr-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );
};

const MenuCategories = () => {
  const { t } = useLanguage();
  const { categories } = useMenuData();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header showBackButton={false} title={restaurantInfo.name} />

      {/* Categories Grid */}
      <main className="flex-1 px-4 py-6 pb-24">
        <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
          {categories.map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} />
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MenuCategories;
